// src/components/homePageComponents/ButtonComponents.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { addToCartAsync } from "../../../features/cartSlice";
import {
  addToWishlistAsync,
  removeFromWishlistAsync,
  fetchWishlist,
} from "../../../features/wishlistSlice";
import { Heart } from "lucide-react";
import { useMemo } from "react";
import { createSelector } from "@reduxjs/toolkit";
import LoginPromptModal from "../../home/authComponents/LoginPromptModal";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ArrowRight } from "lucide-react";


/**
 * SolidButton
 * A reusable solid (primary) button styled with TailwindCSS.
 */
export function SolidButton({
  children,
  onClick,
  disabled = false,
  className = "",
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full
                  py-2
                  rounded-md
                  font-semibold
                  text-white
                  bg-primary-shade3
                  hover:bg-primary-shade4
                  disabled:bg-gray-300
                  disabled:cursor-not-allowed
                  transition-colors
                  duration-200
                  ${className}`}
    >
      {children}
    </button>
  );
}


SolidButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

SolidButton.defaultProps = {
  onClick: () => {},
  disabled: false,
  className: "",
};

/**
 * OutlineButton
 * A reusable outlined (secondary) button styled with TailwindCSS.
 */
export function OutlineButton({
  children,
  onClick,
  disabled = false,
  className = "",
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full
                  py-2
                  rounded-md
                  font-semibold
                  bg-white
                  border-2
                  border-primary-shade3
                  text-primary-shade5
                  hover:bg-primary-shade1
                  hover:border-primary-shade4
                  hover:text-black
                  disabled:bg-gray-100
                  disabled:border-gray-300
                  disabled:text-gray-400
                  disabled:cursor-not-allowed
                  transition-all
                  duration-200
                  ${className}`}
    >
      {children}
    </button>
  );
}


OutlineButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

OutlineButton.defaultProps = {
  onClick: () => {},
  disabled: false,
  className: "",
};

const selectCartItems = createSelector(
  (state) => state.cart.items,
  (items) => items || [] // ensure always array
);

/**
 * AddToCartButton
 * Reusable button to handle "Add to Cart" functionality
 */

export function AddToCartButton({ book, className = "",labelClassName = "" }) {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const cartItems = useSelector(selectCartItems);

  const [loading, setLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const inCart = cartItems?.some((item) => item.book.bookId === book.id);

  const handleClick = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    if (inCart) {
      navigate("/cart");
      return;
    }

    try {
      setLoading(true);
      await dispatch(addToCartAsync({ userId, book })).unwrap();
      toast.success("Added to cart!");
    } catch (err) {
      toast.error(err.message || "Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };

  const ButtonComponent = inCart ? OutlineButton : SolidButton;

  const label = loading ? (
    <span className={`flex items-center justify-center w-full h-full ${labelClassName}`}>Adding…</span>
  ) : inCart ? (
    <span className={`flex items-center justify-center gap-2 w-full h-full ${labelClassName}`}>
      <ShoppingCart size={18} />
      <ArrowRight size={18} />
    </span>
  ) : (
    <span className={`flex items-center justify-center w-full h-full ${labelClassName}`}>Add to Cart</span>
  );



  return (
    <>
      <ButtonComponent
        onClick={handleClick}
        disabled={loading}
        className={`flex items-center justify-center gap-2 ${className}`}
      >
        {label}
      </ButtonComponent>

      {showLoginPrompt && (
        <LoginPromptModal
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </>
  );
}

AddToCartButton.propTypes = {
  book: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
};


export function AddToWishlistButton({ book }) {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.userId);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const wishlistItems = useSelector((state) => state.wishlist.items);

  const [loading, setLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false); // 🔔 state for modal

  const inWishlist = useMemo(() => {
    return wishlistItems?.some((item) => item.book?.bookId === book.id);
  }, [wishlistItems, book.id]);

  const handleClick = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true); // 🔔 Show modal if not logged in
      return;
    }

    try {
      setLoading(true);
      if (inWishlist) {
        await dispatch(
          removeFromWishlistAsync({ userId, bookId: book.id })
        ).unwrap();
        await dispatch(fetchWishlist(userId));
        toast.success("Removed from wishlist!");
      } else {
        await dispatch(addToWishlistAsync({ userId, book })).unwrap();
        await dispatch(fetchWishlist(userId));
        toast.success("Added to wishlist!");
      }
    } catch (err) {
      toast.error(err.message || "Wishlist action failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading}
        className="transition-colors text-primary hover:text-red-500 disabled:text-gray-300"
        title={inWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <Heart
            size={16}
            strokeWidth={2}
            stroke="currentColor"
            fill={inWishlist ? "currentColor" : "none"}
            className={inWishlist ? "text-red-500" : "text-primary"}
          />
        )}
      </button>

      {/* 🔔 Login Modal */}
      {showLoginPrompt && (
        <LoginPromptModal
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </>
  );
}

AddToWishlistButton.propTypes = {
  book: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};
